import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useAuthStore } from '../../../auth/state';
import { 
  FaList, 
  FaCalendarAlt, 
  FaExternalLinkAlt, 
  FaSpinner, 
  FaCrown, 
  FaUsers, 
  FaClock, 
  FaMapMarkerAlt, 
  FaInfoCircle, 
  FaUserFriends,
  FaRegCalendarCheck,
  FaVideo
} from 'react-icons/fa';
import './adm_step1_meet.css';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { db, auth } from '../../../auth/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';

function AdminStep1Meetings() {
  const navigate = useNavigate();
  const { userProfile } = useAuthStore();
  const [activeTab, setActiveTab] = useState(0);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calendarKey, setCalendarKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [hostData, setHostData] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // # Tab configuration
  const tabs = [
    {
      label: 'Meeting List',
      icon: <FaList className="adm_step1_meet_tab_icon" />,
    },
    {
      label: 'Calendar View',
      icon: <FaCalendarAlt className="adm_step1_meet_tab_icon" />,
    }
  ];

  // # Filter meetings based on search
  const filteredMeetings = meetings.filter(meeting => {
    const searchLower = searchQuery.toLowerCase();
    return (
      meeting.title.toLowerCase().includes(searchLower) ||
      meeting.description.toLowerCase().includes(searchLower) ||
      hostData[meeting.host.uid]?.displayName?.toLowerCase().includes(searchLower) ||
      hostData[meeting.host.uid]?.email?.toLowerCase().includes(searchLower)
    );
  });

  // # Fetch user data (hosts and participants)
  const fetchUserData = async (meetings) => {
    // # Get unique user IDs from both hosts and invited participants
    const uniqueUserIds = [...new Set(meetings.flatMap(meeting => 
      [meeting.host.uid, ...meeting.invited]
    ))];

    const userDataPromises = uniqueUserIds.map(async (uid) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          return [uid, {
            displayName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.username || 'Unknown',
            email: data.email || 'No email',
            avatar: data.photoURL || null,
            username: data.username || null
          }];
        }
      } catch (error) {
        console.error(`Error fetching user data for ${uid}:`, error);
      }
      return [uid, { displayName: 'Unknown User', email: 'No email', avatar: null }];
    });

    const userDataResults = await Promise.all(userDataPromises);
    setHostData(Object.fromEntries(userDataResults));
  };

  // # Fetch step1 meetings
  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const step1MeetingsRef = doc(db, 'meetings', 'step1_meetings');
      const docSnap = await getDoc(step1MeetingsRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const meetingsArray = Object.entries(data).map(([id, meeting]) => ({
          id,
          ...meeting,
          meet_time: meeting.meet_time instanceof Date 
            ? meeting.meet_time 
            : typeof meeting.meet_time === 'string' 
              ? new Date(meeting.meet_time) 
              : meeting.meet_time.toDate()
        }));
        meetingsArray.sort((a, b) => b.meet_time - a.meet_time);
        setMeetings(meetingsArray);
        
        // # Fetch data for all users (hosts and participants)
        await fetchUserData(meetingsArray);
      } else {
        setMeetings([]);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
      setError('Failed to load meetings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // # Initial meetings fetch
  useEffect(() => {
    fetchMeetings();
  }, []);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Format date and time
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit'
      })
    };
  };

  // # Update the events mapping to include host data
  const events = meetings.map(meeting => ({
    id: meeting.id,
    title: meeting.title,
    start: meeting.meet_time,
    end: new Date(new Date(meeting.meet_time).getTime() + 3600000),
    hostData: hostData[meeting.host.uid] || {
      displayName: 'Loading...',
      email: '',
      avatar: null,
      username: null
    },
    className: new Date(meeting.meet_time) < new Date() ? 'fc-event-past' : '',
    display: 'block'
  }));

  // # Update calendar options to include custom event rendering
  const calendarOptions = {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay'
    },
    events: events,
    editable: false,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: true,
    height: 'auto',
    aspectRatio: 1.8,
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      meridiem: 'short'
    },
    eventContent: (eventInfo) => {
      const event = eventInfo.event;
      const hostData = event.extendedProps.hostData;
      const hostName = hostData?.displayName || 'Loading...';
      
      return {
        html: `
          <div class="adm_step1_meet_calendar_event">
            <h1 class="adm_step1_meet_calendar_event_title">${event.title}</h1>
            <h2 class="adm_step1_meet_calendar_event_host">
              <span class="adm_step1_meet_calendar_host_icon">👑</span>
              ${hostName}
            </h2>
            <h3 class="adm_step1_meet_calendar_event_time">
              <span class="adm_step1_meet_calendar_time_icon">🕒</span>
              ${event.start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </h3>
          </div>
        `
      };
    },
    slotMinTime: '06:00:00',
    slotMaxTime: '22:00:00',
    allDaySlot: false,
    nowIndicator: true,
    weekNumbers: true,
    weekNumberFormat: { week: 'numeric' },
    dayCellClassNames: 'adm_step1_meet_calendar_cell'
  };

  // # Render loading state
  if (loading) {
    return (
      <div className={`adm_step1_meet_container ${isDarkMode ? 'adm_step1_meet_dark-mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        <div className="adm_step1_meet_loading">
          <FaSpinner className="icon-spin" />
          <p>Loading Step 1 meetings...</p>
        </div>
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    );
  }

  // # Render error state
  if (error) {
    return (
      <div className={`adm_step1_meet_container ${isDarkMode ? 'adm_step1_meet_dark-mode' : ''}`}>
        <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
        <div className="adm_step1_meet_error">
          <p>{error}</p>
        </div>
        <DashboardFooter isDarkMode={isDarkMode} />
      </div>
    );
  }
  
  return (
    <div className={`adm_step1_meet_container ${isDarkMode ? 'adm_step1_meet_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="adm_step1_meet_content">
        <div className="adm_step1_meet_header">
          <div className="adm_step1_meet_title_container">
            <h1>
              <FaVideo style={{ marginRight: '12px', verticalAlign: 'middle' }} />
              Step 1 Mentorship Meetings
            </h1>
            <div className="adm_step1_meet_title_underline"></div>
          </div>
        </div>

        <div className="adm_step1_meet_tabs">
          <Tabs
            selectedIndex={activeTab}
            onSelect={setActiveTab}
            selectedTabClassName="adm_step1_meet_tab--selected"
          >
            <TabList className="adm_step1_meet_tab_list">
              {tabs.map((tab, index) => (
                <Tab 
                  key={index}
                  className={`adm_step1_meet_tab ${activeTab === index ? 'active' : ''}`}
                >
                  {tab.icon}
                  <span className="adm_step1_meet_tab_label">
                    {tab.label}
                    {activeTab === index && (
                      <span className="adm_step1_meet_tab_active_indicator" />
                    )}
                  </span>
                </Tab>
              ))}
            </TabList>

            {/* # Meeting List Tab */}
            <TabPanel className={`adm_step1_meet_tab_panel ${activeTab === 0 ? 'active' : ''}`}>
              <div className="adm_step1_meet_list">
                <div className="adm_step1_meet_search">
                  <input
                    type="text"
                    placeholder="Search meetings by title, description, or host..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="adm_step1_meet_search_input"
                  />
                </div>
                {filteredMeetings.length > 0 ? (
                  filteredMeetings.map(meeting => {
                    const { date, time } = formatDateTime(meeting.meet_time);
                    const host = hostData[meeting.host.uid] || {};
                    return (
                      <div key={meeting.id} className="adm_step1_meet_item">
                        <div className="adm_step1_meet_item_header">
                          <h3>{meeting.title}</h3>
                        </div>
                        <p className="adm_step1_meet_item_desc">
                          <FaInfoCircle style={{ marginRight: '6px', color: '#64748b' }} />
                          {meeting.description}
                        </p>
                        
                        {/* # Host section */}
                        <div className="adm_step1_meet_host_info">
                          {host.avatar ? (
                            <img src={host.avatar} alt={host.displayName} className="adm_step1_meet_host_avatar" />
                          ) : (
                            <div className="adm_step1_meet_host_avatar_placeholder">
                              {host.displayName?.charAt(0) || '?'}
                            </div>
                          )}
                          <div className="adm_step1_meet_host_details">
                            <div className="adm_step1_meet_host_name_container">
                              <span className="adm_step1_meet_host_name">{host.displayName}</span>
                              <span className="adm_step1_meet_host_badge">
                                <FaCrown /> Host
                              </span>
                            </div>
                            <span className="adm_step1_meet_host_email">{host.email}</span>
                          </div>
                        </div>
                        
                        {/* # Participants section */}
                        {meeting.invited.length > 0 && (
                          <div className="adm_step1_meet_participants_container">
                            <div className="adm_step1_meet_participants_header">
                              <FaUserFriends /> <span>Participants ({meeting.invited.length})</span>
                            </div>
                            <div className="adm_step1_meet_participants_list">
                              {meeting.invited.slice(0, 2).map(userId => {
                                const participant = hostData[userId] || {};
                                return (
                                  <div key={userId} className="adm_step1_meet_participant_item">
                                    {participant.avatar ? (
                                      <img src={participant.avatar} alt={participant.displayName} className="adm_step1_meet_participant_avatar" />
                                    ) : (
                                      <div className="adm_step1_meet_participant_avatar_placeholder">
                                        {participant.displayName?.charAt(0) || '?'}
                                      </div>
                                    )}
                                    <span className="adm_step1_meet_participant_name">{participant.displayName}</span>
                                  </div>
                                );
                              })}
                              {meeting.invited.length > 2 && (
                                <div className="adm_step1_meet_more_participants">
                                  +{meeting.invited.length - 2} more
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="adm_step1_meet_item_details">
                          <span className="adm_step1_meet_item_date">
                            <FaRegCalendarCheck /> {date} at {time}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="adm_step1_meet_empty">
                    <FaCalendarAlt className="adm_step1_meet_empty_icon" />
                    <p>No Step 1 meetings found</p>
                  </div>
                )}
              </div>
            </TabPanel>

            {/* # Calendar Tab */}
            <TabPanel className={`adm_step1_meet_tab_panel ${activeTab === 1 ? 'active' : ''}`}>
              <div className="adm_step1_meet_calendar">
                <FullCalendar
                  key={calendarKey}
                  {...calendarOptions}
                />
              </div>
            </TabPanel>
          </Tabs>
        </div>
      </div>
      
      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default AdminStep1Meetings; 